import { createTheme } from '@mui/material/styles';

/**
 * MUI's default theme sets typography.fontFamily to
 * `"Roboto","Helvetica","Arial",sans-serif` and emits it through an emotion
 * class on every text component — which overrides the inherited `body` font
 * rather than falling through to it. Without this theme, MUI-rendered text on
 * the admin pages would ignore the token layer entirely.
 *
 * The value is the custom property, not a repeated font stack, so
 * src/theme/tokens.css stays the single source of truth: emotion emits
 * `font-family: var(--itc-font-body)` and the browser resolves it. Safe because
 * src/index.js imports tokens.css globally, so the property is always defined.
 *
 * Setting the theme is also the correct lever rather than overriding MUI from a
 * stylesheet: MUI injects its emotion classes into <head> after the CRA
 * stylesheet, so a same-specificity CSS rule would lose on order.
 */
const theme = createTheme({
  typography: {
    fontFamily: 'var(--itc-font-body)',
  },
  /*
   * Control styling for the two admin forms. This lives in the theme rather
   * than a stylesheet for the same reason the typography does: MUI's emotion
   * classes land in <head> after the CRA stylesheet, so a same-specificity CSS
   * rule would lose on order.
   *
   * Every value is a custom property, so src/theme/tokens.css stays the single
   * source of truth — emotion emits `var(--itc-*)` and the browser resolves it.
   */
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--itc-radius-sm)',
          backgroundColor: 'var(--itc-surface)',
          transition: 'box-shadow var(--itc-dur-fast) var(--itc-ease)',
          '& fieldset': {
            borderColor: 'var(--itc-border-2)',
          },
          '&:hover fieldset': {
            borderColor: 'var(--itc-gray-400)',
          },
          // Width pinned to 1px so the field does not shift by a pixel on
          // focus — MUI's default bumps the focused border to 2px.
          '&.Mui-focused fieldset': {
            borderColor: 'var(--itc-orange)',
            borderWidth: '1px',
          },
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px var(--itc-focus-ring)',
          },
          '&.Mui-disabled': {
            backgroundColor: 'var(--itc-sunken)',
          },
          // MUI derives error-state colour from theme.palette.error.main, which
          // createTheme computes with color-math (lighten/darken) at theme-build
          // time and so can't take a custom property. Override the rendered
          // state directly instead, same as every other value here.
          '&.Mui-error fieldset': {
            borderColor: 'var(--itc-danger)',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'var(--itc-gray-600)',
          '&.Mui-focused': {
            color: 'var(--itc-gray-700)',
          },
          '&.Mui-error': {
            color: 'var(--itc-danger)',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.Mui-error': {
            color: 'var(--itc-danger)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--itc-radius-sm)',
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
        },
      },
    },
  },
});

export default theme;
